import { useMemo } from "react";
import { AccountRole } from "@common/types/domains/accounting/account";
import { FiltersTypes } from "@common/types/filters/general";
import { DraftAccount, DraftGroup } from "./draftOperations";
import { GetRowCapabilities } from "./editorCapabilities";
import {
    buildChartOfAccountsTemplateEditorRows,
    ChartOfAccountsTemplateEditorRow,
} from "./utils";
import { filterTemplateEditorRows } from "./editorRowFilters";

interface UseTemplateEditorRowsParams {
    accounts: DraftAccount[];
    groups: DraftGroup[];
    searchFilter: string | undefined;
    filters: FiltersTypes;
    getAccountRole?: (account: DraftAccount) => AccountRole | null;
    accountIdsWithEnabledRules?: ReadonlySet<string>;
    getRowCapabilities: GetRowCapabilities;
}

interface TemplateEditorRows {
    rows: ChartOfAccountsTemplateEditorRow[];
    visibleRows: ChartOfAccountsTemplateEditorRow[];
}

// Builds the editor's view rows from the current draft and narrows them to the
// active search filter. Extracted from ChartOfAccountsTemplateEditorContent to
// keep that component focused on wiring + rendering.
export const useTemplateEditorRows = ({
    accounts,
    groups,
    searchFilter,
    filters,
    getAccountRole,
    accountIdsWithEnabledRules,
    getRowCapabilities,
}: UseTemplateEditorRowsParams): TemplateEditorRows => {
    const rows = useMemo(
        () => buildChartOfAccountsTemplateEditorRows({ accounts, groups }),
        [accounts, groups],
    );

    const visibleRows = useMemo(
        () =>
            filterTemplateEditorRows({
                rows,
                filters,
                searchFilter,
                getAccountRole,
                accountIdsWithEnabledRules,
                getRowCapabilities,
            }),
        [
            rows,
            searchFilter,
            filters,
            getAccountRole,
            accountIdsWithEnabledRules,
            getRowCapabilities,
        ],
    );

    return { rows, visibleRows };
};
